import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { approvalsAPI, paymentsAPI, resolveImage } from '../utils/api';
import { tField } from '../utils/bilingualHelper';

export default function Approvals() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('enrollments');
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalFilter, setWithdrawalFilter] = useState('pending'); // 'pending' | 'history' | 'all'
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  }, []);

  const fetchIdRef = React.useRef(0);

  const fetchRequests = useCallback(async (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    try {
      const [reqRes, payRes, withRes] = await Promise.all([
        approvalsAPI.getPending(),
        paymentsAPI.getAll(),
        approvalsAPI.getPendingWithdrawals('all')
      ]);
      if (!isMounted || currentId !== fetchIdRef.current) return;
      setRequests(reqRes.data.requests || []);
      setPayments((payRes.data || []).filter(p => p.verificationStatus === 'Pending'));
      setWithdrawals((withRes.data?.withdrawals || []).filter(w => w.submittedAt));
    } catch (err) {
      if (!isMounted || currentId !== fetchIdRef.current) return;
      console.error(err);
      showToast('Failed to load pending requests.', 'error');
    } finally {
      if (isMounted && currentId === fetchIdRef.current) setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    let isMounted = true;
    fetchRequests(isMounted);
    return () => { isMounted = false; };
  }, [fetchRequests]);

  const handleProcess = async (id, action) => {
    let reason = null;
    if (action === 'Reject') {
      reason = prompt('Please provide a reason for rejection (optional):');
      if (reason === null) return; // User cancelled
    } else {
      if (!window.confirm('Are you sure you want to approve this enrollment?')) return;
    }

    setProcessing(id);
    try {
      await approvalsAPI.process(id, action, reason);
      showToast(`Request ${action.toLowerCase()}ed successfully.`);
      fetchRequests();
    } catch (err) {
      console.error(err);
      showToast(`Failed to ${action.toLowerCase()} request: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handlePaymentProcess = async (id, action) => {
    if (!window.confirm(`Are you sure you want to ${action.toLowerCase()} this payment?`)) return;
    
    setProcessing(id);
    try {
      await paymentsAPI.verify(id, action === 'Approve' ? 'Approved' : 'Rejected', null);
      showToast(`Payment ${action.toLowerCase()}ed successfully.`);
      fetchRequests();
    } catch (err) {
      console.error(err);
      showToast(`Failed to ${action.toLowerCase()} payment: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleWithdrawalProcess = async (studentId, action, isSpecific = false, targetNames = '') => {
    let adminNote = null;
    if (action === 'Reject') {
      adminNote = prompt(
        isSpecific
          ? t('approvals.dropRejectPrompt', 'Please provide a reason for rejecting this course drop notice (optional) / 請輸入駁回退選原因（選填）:')
          : t('approvals.withdrawalRejectPrompt', 'Please provide a reason for rejecting this notice (optional) / 請輸入駁回原因（選填）:')
      );
      if (adminNote === null) return; // User cancelled
    } else {
      const confirmMsg = isSpecific
        ? t('approvals.dropApproveConfirm', `Are you sure you want to approve dropping ${targetNames || 'selected class'}? Student will remain active in other classes. / 確定要確認批准退選 ${targetNames || '指定課程'} 嗎？學員將保留在其他班級中正常在讀。`)
        : t('approvals.withdrawalApproveConfirm', 'Are you sure you want to approve this withdrawal notice? Student will be withdrawn from all classes. / 確定要確認批准此全校退學申請嗎？學員將從所有班級中退學。');
      if (!window.confirm(confirmMsg)) return;
    }

    setProcessing(studentId);
    try {
      await approvalsAPI.processWithdrawal(studentId, action, adminNote);
      showToast(
        action === 'Approve'
          ? (isSpecific ? 'Course drop approved successfully.' : 'Withdrawal notice approved successfully.')
          : 'Notice rejected/cancelled successfully.'
      );
      fetchRequests();
    } catch (err) {
      console.error(err);
      showToast(`Failed to ${action.toLowerCase()} withdrawal: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setProcessing(null);
    }
  };


  const pendingWithdrawals = withdrawals.filter(w => w.status === 'Pending');
  const historyWithdrawals = withdrawals.filter(w => w.status !== 'Pending');
  const displayedWithdrawals = withdrawalFilter === 'pending'
    ? pendingWithdrawals
    : (withdrawalFilter === 'history' ? historyWithdrawals : withdrawals);

  return (
    <Layout title={t('approvals.title', 'Pending Approvals')}>

      {/* Receipt Lightbox Modal */}
      {previewReceipt && (
        <div
          onClick={() => setPreviewReceipt(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setPreviewReceipt(null)}
            style={{
              position: 'absolute', top: '16px', right: '20px',
              background: 'rgba(255,255,255,0.15)',
              border: '2px solid rgba(255,255,255,0.4)',
              borderRadius: '50%', width: '44px', height: '44px',
              fontSize: '22px', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1, backdropFilter: 'blur(4px)',
            }}
            title="Close"
          >
            ×
          </button>
          {/* Image (stop propagation so clicking image doesn't close) */}
          <img
            src={previewReceipt}
            alt="Payment Receipt"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '90vw', maxHeight: '90vh',
              borderRadius: '10px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              objectFit: 'contain',
            }}
          />
          <div style={{ position: 'absolute', bottom: '16px', color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
            Click anywhere outside the image to close
          </div>
        </div>
      )}

      <div className="section-header">
        <h2 className="section-title">{t('approvals.title', 'Pending Approvals')}</h2>
        <p className="text-muted">{t('approvals.subtitle', 'Review and approve requests originating from the mobile app.')}</p>
      </div>

      <div style={{ display: 'flex', gap: '16px', borderBottom: '1px solid #ddd', paddingBottom: '10px', marginBottom: '20px' }}>
        <button 
          style={{ 
            background: 'transparent', 
            border: 'none', 
            fontSize: '16px', 
            fontWeight: activeTab === 'enrollments' ? 'bold' : 'normal',
            color: activeTab === 'enrollments' ? '#0056b3' : '#666',
            cursor: 'pointer',
            borderBottom: activeTab === 'enrollments' ? '2px solid #0056b3' : 'none',
            paddingBottom: '4px'
          }}
          onClick={() => setActiveTab('enrollments')}
        >
          {t('approvals.enrollments', 'Enrollments')} ({requests.length})
        </button>
        <button 
          style={{ 
            background: 'transparent', 
            border: 'none', 
            fontSize: '16px', 
            fontWeight: activeTab === 'payments' ? 'bold' : 'normal',
            color: activeTab === 'payments' ? '#0056b3' : '#666',
            cursor: 'pointer',
            borderBottom: activeTab === 'payments' ? '2px solid #0056b3' : 'none',
            paddingBottom: '4px'
          }}
          onClick={() => setActiveTab('payments')}
        >
          {t('approvals.feePayments', 'Fee Payments')} ({payments.length})
        </button>
        <button 
          style={{ 
            background: 'transparent', 
            border: 'none', 
            fontSize: '16px', 
            fontWeight: activeTab === 'withdrawals' ? 'bold' : 'normal',
            color: activeTab === 'withdrawals' ? '#922b21' : '#666',
            cursor: 'pointer',
            borderBottom: activeTab === 'withdrawals' ? '2px solid #922b21' : 'none',
            paddingBottom: '4px'
          }}
          onClick={() => setActiveTab('withdrawals')}
        >
          {t('approvals.withdrawals', 'Withdrawal Notices / 退學申請')} ({pendingWithdrawals.length})
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '20px', color: '#666' }}>{t('approvals.loading', 'Loading requests...')}</div>
      ) : activeTab === 'enrollments' ? (
        requests.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', background: '#fff', borderRadius: '8px', border: '1px solid #eee' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <h3>{t('approvals.allCaughtUp', 'All Caught Up!')}</h3>
            <p>{t('approvals.noEnrollments', 'There are currently no pending enrollment requests.')}</p>
          </div>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {requests.map((req, idx) => {
            const student = req.studentId || {};
            const batch = req.batchId || {};
            const meta = req.metadata || {};
            const months = meta.months || [];
            const merch = meta.purchasedMerchandise || [];
            
            const paymentMethod = meta.paymentMethod;
            const amountPerMonth = meta.amountPerMonth;
            const merchandiseFee = meta.merchandiseFee;
            const receiptUrl = meta.receiptUrl;
            
            const reqId = req._id || req.id || idx;

            return (
              <div key={reqId} style={{
                background: '#fff',
                border: '1px solid #e0d8ce',
                padding: '24px',
                borderRadius: '8px',
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap'
              }}>
                {/* Student Info */}
                <div style={{ flex: '1 1 250px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>🎓</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{tField(student.name) || 'Unknown Student'}</h3>
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    <strong>Email:</strong> {student.email || 'N/A'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    <strong>Mobile:</strong> {student.mobile || 'N/A'}
                  </div>
                  {(student.dob || student.gender) && (
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                      <strong>Details:</strong> {student.gender || 'Unknown'} {student.dob ? `(DOB: ${new Date(student.dob).toLocaleDateString()})` : ''}
                    </div>
                  )}
                  {((student.medicalNote?.en || student.medicalNote?.zh) || (typeof student.medicalNote === 'string' && student.medicalNote)) && (
                    <div style={{ fontSize: '13px', color: '#c0392b', marginBottom: '8px', background: '#fcecec', padding: '4px 8px', borderRadius: '4px' }}>
                      <strong>Medical Note:</strong> {(student.medicalNote?.en || student.medicalNote)}
                    </div>
                  )}
                  
                  {/* Parent Info */}
                  {student.parentId && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #eee' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '16px' }}>👨‍👩‍👧</span>
                        <strong style={{ fontSize: '14px', color: '#444' }}>Parent / Guardian</strong>
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                        <div><strong>Name:</strong> {tField(student.parentId.name) || 'N/A'} ({student.parentId.relationToStudent || 'Relation Unknown'})</div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                        <strong>Mobile:</strong> {student.parentId.mobile || 'N/A'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        <strong>Email:</strong> {student.parentId.email || 'N/A'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Batch & Enrollment Details */}
                <div style={{ flex: '2 1 300px', borderLeft: '1px solid #eee', paddingLeft: '24px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ color: '#111' }}>Class: </strong>
                    <span style={{ color: '#0056b3', fontWeight: 500 }}>{tField(batch.name) || 'Unknown Class'}</span>
                  </div>
                  
                  {paymentMethod && (
                    <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                      <strong style={{ color: '#111' }}>Payment Method: </strong>
                      <span style={{ color: '#333' }}>{paymentMethod}</span>
                    </div>
                  )}

                  {amountPerMonth > 0 && (
                    <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                      <strong style={{ color: '#111' }}>Monthly Fee: </strong>
                      <span style={{ color: '#333' }}>${parseFloat(amountPerMonth).toFixed(2)}</span>
                    </div>
                  )}

                  {months.length > 0 && (
                    <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                      <strong style={{ color: '#111' }}>Selected Months: </strong>
                      <span style={{ background: '#eef2f6', padding: '4px 8px', borderRadius: '4px', color: '#333' }}>
                        {months.join(', ')}
                      </span>
                    </div>
                  )}

                  {merch.length > 0 && (
                    <div style={{ fontSize: '14px' }}>
                      <strong style={{ color: '#111' }}>Merchandise Requested: </strong>
                      <ul style={{ margin: '4px 0 0 20px', padding: 0, color: '#555' }}>
                        {merch.map((m, idx) => (
                          <li key={idx}>
                            {tField(m.name)} <span style={{ color: '#999' }}>x{m.quantity}</span> ({m.size || 'Standard'})
                          </li>
                        ))}
                      </ul>
                      {merchandiseFee > 0 && (
                        <div style={{ marginTop: '4px', color: '#333', fontWeight: 500 }}>
                          Merch Fee: ${parseFloat(merchandiseFee).toFixed(2)}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>
                    Submitted on: {new Date(req.createdAt).toLocaleString()}
                  </div>

                  {/* Payment Receipt */}
                  {receiptUrl && (
                    <div style={{ marginTop: '16px', padding: '12px', background: '#f0fff4', borderRadius: '8px', border: '1px solid #9ae6b4' }}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: '#276749', marginBottom: '8px' }}>
                        📄 Payment Receipt Attached
                      </div>
                      <img
                        src={resolveImage(receiptUrl)}
                        alt="Payment Receipt"
                        onClick={() => setPreviewReceipt(resolveImage(receiptUrl))}
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '6px', cursor: 'zoom-in', border: '1px solid #9ae6b4' }}
                      />
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>Click image to preview full receipt</div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
                  <button 
                    className="btn btn-primary"
                    disabled={processing === reqId}
                    onClick={() => handleProcess(req._id || req.id, 'Approve')}
                    style={{ minWidth: '120px' }}
                  >
                    {processing === reqId ? t('approvals.processing', 'Processing...') : `✅ ${t('approvals.approve', 'Approve')}`}
                  </button>
                  <button 
                    className="btn btn-outline"
                    disabled={processing === reqId}
                    onClick={() => handleProcess(req._id || req.id, 'Reject')}
                    style={{ minWidth: '120px', borderColor: '#dc3545', color: '#dc3545' }}
                  >
                    ❌ {t('approvals.reject', 'Reject')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )
      ) : activeTab === 'payments' ? (
        payments.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', background: '#fff', borderRadius: '8px', border: '1px solid #eee' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <h3>{t('approvals.allCaughtUp', 'All Caught Up!')}</h3>
            <p>{t('approvals.noPayments', 'There are currently no pending fee payments.')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {payments.map((payment) => (
              <div key={payment._id || payment.id || Math.random()} style={{
                background: '#fff',
                border: '1px solid #e0d8ce',
                padding: '24px',
                borderRadius: '8px',
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap'
              }}>
                <div style={{ flex: '1 1 250px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>💳</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                      {typeof payment.student === 'object' && payment.student ? tField(payment.student.name) : tField(payment.studentName) || 'Unknown Student'}
                    </h3>
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    <strong>Student ID:</strong> {typeof payment.student === 'object' && payment.student ? payment.student.studentId || payment.studentId : (payment.studentId || (typeof payment.student === 'string' ? payment.student : 'N/A'))}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    <strong>Class:</strong> <span style={{ color: '#0056b3', fontWeight: 500 }}>
                      {typeof payment.batch === 'object' && payment.batch ? tField(payment.batch.name) : (tField(payment.batchName) || (typeof payment.batch === 'string' ? payment.batch : 'Unknown Class'))}
                    </span>
                  </div>
                  {payment.month && (
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                      <strong>Month:</strong> {payment.month}
                    </div>
                  )}
                  {payment.student && typeof payment.student === 'object' && (
                    payment.student.status === 'Withdrawn' ? (
                      <div style={{ marginTop: '8px' }}>
                        <span style={{ 
                          display: 'inline-block',
                          background: '#f8d7da', 
                          color: '#721c24', 
                          padding: '3px 8px', 
                          borderRadius: '4px', 
                          fontSize: '11px', 
                          fontWeight: 600,
                          border: '1px solid #f5c6cb'
                        }}>
                          ⚠️ Student Withdrawn / 已退學
                        </span>
                      </div>
                    ) : payment.student.resignationNotice?.status === 'Pending' ? (
                      <div style={{ marginTop: '8px' }}>
                        <span style={{ 
                          display: 'inline-block',
                          background: '#fff3cd', 
                          color: '#856404', 
                          padding: '3px 8px', 
                          borderRadius: '4px', 
                          fontSize: '11px', 
                          fontWeight: 600,
                          border: '1px solid #ffeeba'
                        }}>
                          ⚠️ Withdrawal Notice Pending / 退學申請中 ({payment.student.resignationNotice.effectiveDate ? `Effective: ${payment.student.resignationNotice.effectiveDate}` : 'Pending Review'})
                        </span>
                      </div>
                    ) : null
                  )}
                </div>

                <div style={{ flex: '2 1 300px', borderLeft: '1px solid #eee', paddingLeft: '24px' }}>
                  <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                    <strong style={{ color: '#111' }}>Amount: </strong>
                    <span style={{ color: '#333', fontSize: '16px', fontWeight: 'bold' }}>HK${parseFloat(payment.amount).toFixed(2)}</span>
                  </div>
                  <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                    <strong style={{ color: '#111' }}>Method: </strong>
                    <span style={{ color: '#333' }}>{payment.paymentMethod || 'Unknown'}</span>
                  </div>
                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>
                    Submitted on: {new Date(payment.createdAt).toLocaleString()}
                  </div>

                  {payment.receiptUrl && (
                    <div style={{ marginTop: '16px', padding: '12px', background: '#f0fff4', borderRadius: '8px', border: '1px solid #9ae6b4' }}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: '#276749', marginBottom: '8px' }}>
                        📄 Payment Receipt Attached
                      </div>
                      <img
                        src={resolveImage(payment.receiptUrl)}
                        alt="Payment Receipt"
                        onClick={() => setPreviewReceipt(resolveImage(payment.receiptUrl))}
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '6px', cursor: 'zoom-in', border: '1px solid #9ae6b4' }}
                      />
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>Click image to preview full receipt</div>
                    </div>
                  )}
                </div>

                <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
                  <button 
                    className="btn btn-primary"
                    disabled={processing === (payment._id || payment.id)}
                    onClick={() => handlePaymentProcess(payment._id || payment.id, 'Approve')}
                    style={{ minWidth: '120px' }}
                  >
                    {processing === (payment._id || payment.id) ? t('approvals.processing', 'Processing...') : `✅ ${t('approvals.approve', 'Approve')}`}
                  </button>
                  <button 
                    className="btn btn-outline"
                    disabled={processing === (payment._id || payment.id)}
                    onClick={() => handlePaymentProcess(payment._id || payment.id, 'Reject')}
                    style={{ minWidth: '120px', borderColor: '#dc3545', color: '#dc3545' }}
                  >
                    ❌ {t('approvals.reject', 'Reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* activeTab === 'withdrawals' */
        <div>
          {/* Sub-filter tabs */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'inline-flex', background: '#f0f0f0', padding: '4px', borderRadius: '8px', gap: '4px' }}>
              <button
                type="button"
                onClick={() => setWithdrawalFilter('pending')}
                style={{
                  background: withdrawalFilter === 'pending' ? '#fff' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: withdrawalFilter === 'pending' ? 700 : 500,
                  color: withdrawalFilter === 'pending' ? '#1c1c1e' : '#666',
                  boxShadow: withdrawalFilter === 'pending' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  cursor: 'pointer'
                }}
              >
                ⏳ {t('approvals.pendingFilter', 'Pending / 待審批')} ({pendingWithdrawals.length})
              </button>
              <button
                type="button"
                onClick={() => setWithdrawalFilter('history')}
                style={{
                  background: withdrawalFilter === 'history' ? '#fff' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: withdrawalFilter === 'history' ? 700 : 500,
                  color: withdrawalFilter === 'history' ? '#1c1c1e' : '#666',
                  boxShadow: withdrawalFilter === 'history' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  cursor: 'pointer'
                }}
              >
                📜 {t('approvals.historyFilter', 'History / 已處理記錄')} ({historyWithdrawals.length})
              </button>
              <button
                type="button"
                onClick={() => setWithdrawalFilter('all')}
                style={{
                  background: withdrawalFilter === 'all' ? '#fff' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: withdrawalFilter === 'all' ? 700 : 500,
                  color: withdrawalFilter === 'all' ? '#1c1c1e' : '#666',
                  boxShadow: withdrawalFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  cursor: 'pointer'
                }}
              >
                {t('approvals.allFilter', 'All / 全部')} ({withdrawals.length})
              </button>
            </div>
            {withdrawalFilter === 'history' && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                Showing previously processed withdrawal notices and course drops.
              </span>
            )}
          </div>

          {displayedWithdrawals.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', background: '#fff', borderRadius: '8px', border: '1px solid #eee' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
              <h3>{t('approvals.allCaughtUp', 'All Caught Up!')}</h3>
              <p>
                {withdrawalFilter === 'pending'
                  ? t('approvals.noWithdrawals', 'There are currently no pending withdrawal notices. / 目前沒有待處理的退學申請。')
                  : t('approvals.noHistoryWithdrawals', 'No processed withdrawal notices found in this filter.')}
              </p>
              {withdrawalFilter === 'pending' && historyWithdrawals.length > 0 && (
                <button
                  type="button"
                  onClick={() => setWithdrawalFilter('history')}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    background: '#f5f5f7',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#333',
                    cursor: 'pointer'
                  }}
                >
                  📜 {t('approvals.viewHistoryBtn', `View ${historyWithdrawals.length} Processed Notice(s) in History / 查看已處理記錄`)} →
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {displayedWithdrawals.map((item) => {
                const studentId = item.studentId || item._id;
                const studentName = tField(item.studentName) || 'Student';
                const parent = item.parent || {};
                const parentPhone = parent.phone || '';
                const cleanPhone = parentPhone.replace(/[^0-9]/g, '');
                const parentName = tField(parent.name) || 'Parent';
                const effectiveDate = item.effectiveDate || 'End of Term';
                const reasonText = tField(item.reason) || (typeof item.reason === 'string' ? item.reason : 'Not specified');
                const parentNoteText = tField(item.parentNote) || (typeof item.parentNote === 'string' ? item.parentNote : '');

                const withdrawalType = item.withdrawalType || 'all';
                const isSpecific = withdrawalType === 'specific';
                const targetBatchIds = (item.targetBatchIds || []).map(id => id.toString());
                const targetBatchNames = item.targetBatchNames || {};
                const targetNamesStr = tField(targetBatchNames) || targetBatchNames.en || targetBatchNames.zh || 'Specified Course';
                const isApproved = item.status === 'Approved';
                const isRejected = item.status === 'Rejected';
                const isPending = item.status === 'Pending';

                const waMessage = encodeURIComponent(
                  isSpecific
                    ? `Hello ${parentName}, this is ETS Academy regarding the course drop notice for ${studentName} (${targetNamesStr}) effective ${effectiveDate}. / 您好，ETS Academy 就學員 ${studentName} 於 ${effectiveDate} 生效的課程退選申請（${targetNamesStr}）聯絡閣下。`
                    : `Hello ${parentName}, this is ETS Academy regarding the withdrawal notice for ${studentName} effective ${effectiveDate}. / 您好，ETS Academy 就學員 ${studentName} 於 ${effectiveDate} 生效的退學申請聯絡閣下。`
                );
                const waUrl = cleanPhone ? `https://wa.me/${cleanPhone.startsWith('852') ? cleanPhone : '852' + cleanPhone}?text=${waMessage}` : null;

                return (
                  <div key={studentId} style={{
                    background: '#fff',
                    border: isSpecific ? '1px solid #d0e2f5' : '1px solid #e8d7d8',
                    borderLeft: isSpecific ? '5px solid #1976d2' : '5px solid #922b21',
                    padding: '24px',
                    borderRadius: '8px',
                    display: 'flex',
                    gap: '24px',
                    flexWrap: 'wrap',
                    boxShadow: isSpecific ? '0 2px 8px rgba(25, 118, 210, 0.05)' : '0 2px 8px rgba(146, 43, 33, 0.05)'
                  }}>
                    {/* Left Column: Student & Parent Info */}
                    <div style={{ flex: '1 1 280px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '22px' }}>{isSpecific ? '📘' : '🚪'}</span>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1c1c1e' }}>
                            {studentName} {item.studentCode ? `(${item.studentCode})` : ''}
                          </h3>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                            {isApproved && (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                background: '#e8f8f0',
                                color: '#27ae60',
                                fontSize: '11px',
                                fontWeight: 700,
                                border: '1px solid #b8e6ca'
                              }}>
                                ✅ Approved / 已批准
                              </span>
                            )}
                            {isRejected && (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                background: '#fdeeed',
                                color: '#c0392b',
                                fontSize: '11px',
                                fontWeight: 700,
                                border: '1px solid #f8c9c5'
                              }}>
                                ❌ Rejected / 已駁回
                              </span>
                            )}
                            {isPending && (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                background: '#fef5e7',
                                color: '#d35400',
                                fontSize: '11px',
                                fontWeight: 700,
                                border: '1px solid #fcdcb2'
                              }}>
                                ⏳ Pending Review / 待審批
                              </span>
                            )}
                            {isSpecific ? (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                background: '#e8f4fd',
                                color: '#1565c0',
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.3px',
                                border: '1px solid #b3d7ff'
                              }}>
                                📘 Drop Class / 退選課程
                              </span>
                            ) : (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                background: '#fbeee6',
                                color: '#b03a2e',
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.3px',
                                border: '1px solid #f5c6cb'
                              }}>
                                🏛️ Full Withdrawal / 全校退學
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Enrolled Batches */}
                      {item.batches && item.batches.length > 0 && (
                        <div style={{ marginBottom: '12px', background: '#faf8f8', padding: '10px 12px', borderRadius: '6px' }}>
                          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#888', fontWeight: 600, marginBottom: '6px' }}>
                            Enrolled Classes / 在讀班級
                          </div>
                          {item.batches.map((b, bIdx) => {
                            const bId = (b._id || b.id || '').toString();
                            const isTarget = isSpecific && targetBatchIds.includes(bId);
                            return (
                              <div key={b._id || bIdx} style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#333',
                                marginBottom: '6px',
                                padding: '4px 6px',
                                borderRadius: '4px',
                                background: isTarget ? '#fdeeed' : (isSpecific ? '#edf7ed' : 'transparent')
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                  <span>• {tField(b.name) || 'Class'}</span>
                                  {isSpecific && (
                                    <span style={{
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      padding: '1px 6px',
                                      borderRadius: '4px',
                                      background: isTarget ? '#f8d7da' : '#d4edda',
                                      color: isTarget ? '#721c24' : '#155724'
                                    }}>
                                      {isTarget ? '退選 / Dropping' : '保留 / Continuing'}
                                    </span>
                                  )}
                                </div>
                                {b.days && <div style={{ fontSize: '11px', color: '#666', fontWeight: 400, marginLeft: '10px' }}>({b.days.join(', ')})</div>}
                                {(b.startDate || b.endDate) && (
                                  <div style={{ fontSize: '11px', color: '#777', fontWeight: 400, marginLeft: '10px' }}>
                                    Term: {b.startDate ? new Date(b.startDate).toLocaleDateString() : 'Start'} ~ {b.endDate ? new Date(b.endDate).toLocaleDateString() : 'Ongoing'}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Parent Contact Card */}
                      <div style={{ borderTop: '1px dashed #eee', paddingTop: '10px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#555', marginBottom: '4px' }}>
                          👨‍👩‍👧 Parent / Guardian / 家長聯絡:
                        </div>
                        <div style={{ fontSize: '13px', color: '#333', marginBottom: '3px' }}>
                          <strong>Name:</strong> {parentName}
                        </div>
                        {parentPhone && (
                          <div style={{ fontSize: '13px', color: '#333', marginBottom: '3px' }}>
                            <strong>Mobile:</strong> {parentPhone}
                          </div>
                        )}
                        {parent.email && (
                          <div style={{ fontSize: '13px', color: '#666', marginBottom: '3px' }}>
                            <strong>Email:</strong> {parent.email}
                          </div>
                        )}
                        {waUrl && (
                          <a
                            href={waUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '8px',
                              padding: '6px 12px',
                              background: '#25D366',
                              color: '#fff',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                          >
                            💬 WhatsApp Parent / 聯絡家長
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Middle Column: Withdrawal Reason & Dates */}
                    <div style={{ flex: '2 1 320px', borderLeft: '1px solid #eee', paddingLeft: '24px' }}>
                      {isSpecific && (
                        <div style={{
                          marginBottom: '14px',
                          padding: '10px 14px',
                          background: '#e8f4fd',
                          borderLeft: '4px solid #1976d2',
                          borderRadius: '6px'
                        }}>
                          <span style={{ fontSize: '11px', color: '#1565c0', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>
                            Target Course to Drop / 申請退選之課程:
                          </span>
                          <div style={{ fontSize: '15px', fontWeight: 700, color: '#0d47a1', marginTop: '3px' }}>
                            🎯 {targetNamesStr}
                          </div>
                          <div style={{ fontSize: '11px', color: '#555', marginTop: '3px' }}>
                            Student will remain active in all other enrolled classes. / 學員將繼續保留在其他班級中正常在讀。
                          </div>
                        </div>
                      )}

                      <div style={{ marginBottom: '14px' }}>
                        <span style={{ fontSize: '12px', color: '#777', textTransform: 'uppercase', fontWeight: 600 }}>
                          {isSpecific ? 'Effective Drop Date / 退選生效日期:' : 'Effective Discontinuation Date / 退學生效日期:'}
                        </span>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: isSpecific ? '#1565c0' : '#922b21', marginTop: '2px' }}>
                          📅 {effectiveDate}
                        </div>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#777', textTransform: 'uppercase', fontWeight: 600 }}>
                          Reason for Withdrawal / 退學原因:
                        </span>
                        <div style={{
                          marginTop: '4px',
                          display: 'inline-block',
                          background: '#f8d7da',
                          color: '#721c24',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 600
                        }}>
                          {reasonText}
                        </div>
                      </div>

                      {parentNoteText && (
                        <div style={{ marginTop: '12px', background: '#fef9e7', border: '1px solid #f9e79f', padding: '12px', borderRadius: '6px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#7d6608', marginBottom: '4px' }}>
                            📝 Parent's Note / 家長備註說明:
                          </div>
                          <div style={{ fontSize: '13px', color: '#4d3d04', lineHeight: 1.5 }}>
                            "{parentNoteText}"
                          </div>
                        </div>
                      )}

                      {item.submittedAt && (
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '14px' }}>
                          Submitted on: {new Date(item.submittedAt).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {/* Right Column: Approval Action Buttons or Status Box */}
                    <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
                      {isPending ? (
                        <>
                          <button 
                            className="btn"
                            disabled={processing === studentId}
                            onClick={() => handleWithdrawalProcess(studentId, 'Approve', isSpecific, targetNamesStr)}
                            style={{
                              minWidth: '150px',
                              background: '#27ae60',
                              color: '#fff',
                              border: 'none',
                              padding: '10px 16px',
                              borderRadius: '6px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            {processing === studentId ? 'Processing...' : (isSpecific ? '✅ Approve Drop / 批准退選' : '✅ Approve / 確認退學')}
                          </button>
                          <button 
                            className="btn btn-outline"
                            disabled={processing === studentId}
                            onClick={() => handleWithdrawalProcess(studentId, 'Reject', isSpecific, targetNamesStr)}
                            style={{
                              minWidth: '150px',
                              borderColor: '#c0392b',
                              color: '#c0392b',
                              padding: '10px 16px',
                              borderRadius: '6px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            ❌ Reject / 駁回通知
                          </button>
                        </>
                      ) : isApproved ? (
                        <div style={{
                          padding: '14px 18px',
                          background: '#e8f8f0',
                          borderRadius: '8px',
                          border: '1px solid #b8e6ca',
                          textAlign: 'center',
                          minWidth: '160px'
                        }}>
                          <div style={{ color: '#27ae60', fontWeight: 700, fontSize: '14px' }}>
                            ✅ Approved / 已批准
                          </div>
                          {item.processedAt && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                              {new Date(item.processedAt).toLocaleString()}
                            </div>
                          )}
                          {item.adminNote && (
                            <div style={{ fontSize: '11px', color: '#333', marginTop: '6px', fontStyle: 'italic' }}>
                              "{item.adminNote}"
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{
                          padding: '14px 18px',
                          background: '#fdeeed',
                          borderRadius: '8px',
                          border: '1px solid #f8c9c5',
                          textAlign: 'center',
                          minWidth: '160px'
                        }}>
                          <div style={{ color: '#c0392b', fontWeight: 700, fontSize: '14px' }}>
                            ❌ Rejected / 已駁回
                          </div>
                          {item.processedAt && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                              {new Date(item.processedAt).toLocaleString()}
                            </div>
                          )}
                          {item.adminNote && (
                            <div style={{ fontSize: '11px', color: '#333', marginTop: '6px', fontStyle: 'italic' }}>
                              "{item.adminNote}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {toast.show && (
        <div style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          padding: '16px 24px',
          backgroundColor: toast.type === 'error' ? '#f56565' : '#48bb78',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <span>{toast.message}</span>
          <button 
            onClick={() => setToast({ show: false, message: '', type: '' })}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', padding: 0 }}
          >
            ×
          </button>
        </div>
      )}
    </Layout>
  );
}
